import {
  DEFAULT_AGGREGATION,
  KPI_AGGREGATION_LABELS,
  KPI_AGGREGATIONS,
  KPI_DATA_SOURCE_LABELS,
  KPI_DATA_SOURCES,
  KPI_DIRECTION_LABELS,
  KPI_DIRECTIONS,
  KPI_KIND_LABELS,
  KPI_KINDS,
  KPI_PERIODICITIES,
  KPI_PERIODICITY_LABELS,
  KPI_UNIT_LABELS,
  KPI_UNITS,
  calculationFormulaReferences,
  formatDate,
  kpiCustomUnitInputSchema,
  kpiCustomUnitSchema,
  kpiInputSchema,
  kpiListSchema,
  kpiOptionsSchema,
  kpiSchema,
  kpiUnitLabel,
  type Kpi,
  type KpiAggregationName,
  type KpiCustomUnit,
  type KpiDataSourceName,
  type KpiDirectionName,
  type KpiInput,
  type KpiKindName,
  type KpiOptions,
  type KpiPeriodicityName,
  type KpiUnitName,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  CalendarRange,
  FolderKanban,
  Gauge,
  History,
  Lock,
  Pencil,
  Plus,
  Search,
  Target,
  Trash2,
  UserRound,
  Users,
} from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { AuditLink } from '@/features/audit/AuditLink';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const PAGE_SIZE = 50;
const formatPercent = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
const formatNumber = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/** Indicadores › KPIs (T-060, RF-21, PRD §11.11): cadastro dos indicadores do projeto. */
export function KpisPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Indicadores (KPIs)"
        description="Os indicadores que medem os objetivos: como cada um é calculado, quem responde por ele, a direção desejada e a regra de semáforo."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os indicadores pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <KpisSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function KpisSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can = (action: 'create' | 'edit' | 'delete') => !readOnly && canDo('indicadores', action);
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const base = `/organizations/${organizationId}/projects/${projectId}/kpis`;
  // Vindo da busca global (?q=), a lista já abre filtrada.
  const [search, setSearch] = useState(() => params.get('q')?.trim().slice(0, 100) ?? '');
  const [q, setQ] = useState(search);
  const [perspectiveId, setPerspectiveId] = useState('');
  // Vindo do quadro do BSC ("indicadores vinculados"), a lista já abre filtrada pelo objetivo.
  const [objectiveId, setObjectiveId] = useState(params.get('objetivo') ?? '');
  const [departmentId, setDepartmentId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [kind, setKind] = useState<KpiKindName | ''>('');
  const [direction, setDirection] = useState<KpiDirectionName | ''>('');
  const [dataSource, setDataSource] = useState<KpiDataSourceName | ''>('');
  const [periodicity, setPeriodicity] = useState<KpiPeriodicityName | ''>('');
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(1);
  // Com a lista já aberta, um novo ?q= (outro resultado da busca) vale; lido, sai do endereço.
  useEffect(() => {
    const linked = params.get('q');
    if (linked === null) return;
    const next = linked.trim().slice(0, 100);
    setSearch(next);
    setQ(next);
    setPage(1);
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('q');
        return out;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const [editing, setEditing] = useState<Kpi | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Kpi | null>(null);
  const ids = {
    search: useId(),
    perspective: useId(),
    objective: useId(),
    department: useId(),
    owner: useId(),
    kind: useId(),
    direction: useId(),
    source: useId(),
    periodicity: useId(),
  };

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  const options = useQuery({
    queryKey: ['kpis', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: kpiOptionsSchema, signal }),
  });
  const filters = {
    q,
    perspectiveId,
    objectiveId,
    departmentId,
    ownerId,
    kind,
    direction,
    dataSource,
    periodicity,
    archived,
    page,
  };
  const list = useQuery({
    queryKey: ['kpis', projectId, 'list', filters],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: {
          q: q || undefined,
          perspectiveId: perspectiveId || undefined,
          objectiveId: objectiveId || undefined,
          departmentId: departmentId || undefined,
          ownerId: ownerId || undefined,
          kind: kind || undefined,
          direction: direction || undefined,
          dataSource: dataSource || undefined,
          periodicity: periodicity || undefined,
          archived: archived ? 'true' : undefined,
          page,
          pageSize: PAGE_SIZE,
        },
        schema: kpiListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['kpis', projectId] });

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (k: Kpi) =>
      client.request(`${base}/${k.id}/${k.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: kpiSchema,
      }),
    onSuccess: async (k) => {
      setArchiving(null);
      toast.success(k.archivedAt ? 'Indicador arquivado' : 'Indicador restaurado', { description: k.name });
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: KpiInput) {
    const target = editing === 'new' ? null : editing;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: kpiSchema,
    });
    setEditing(null);
    toast.success(target ? 'Indicador salvo' : 'Indicador cadastrado', { description: saved.name });
    await refresh();
  }

  const opts = options.data;
  const filtered = Boolean(
    q ||
    perspectiveId ||
    objectiveId ||
    departmentId ||
    ownerId ||
    kind ||
    direction ||
    dataSource ||
    periodicity,
  );
  const clearFilters = () => {
    setSearch('');
    setPerspectiveId('');
    setObjectiveId('');
    setDepartmentId('');
    setOwnerId('');
    setKind('');
    setDirection('');
    setDataSource('');
    setPeriodicity('');
    setPage(1);
  };
  const onFilter =
    <T,>(set: (v: T) => void) =>
    (v: string) => {
      set(v as T);
      setPage(1);
    };

  return (
    <div className="mt-6 flex flex-col gap-5">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: os indicadores ficam disponíveis só para consulta.
        </p>
      ) : null}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <label htmlFor={ids.search} className="sr-only">
              Buscar indicador
            </label>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id={ids.search}
              type="search"
              placeholder="Buscar pelo nome ou código"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-9"
            />
          </div>
          {can('create') && !archived ? (
            <Button className="h-11" onClick={() => setEditing('new')} disabled={!opts}>
              <Plus aria-hidden />
              Novo indicador
            </Button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Filter
            id={ids.perspective}
            label="Perspectiva"
            value={perspectiveId}
            onChange={onFilter(setPerspectiveId)}
          >
            <option value="">Todas as perspectivas</option>
            {opts?.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Filter>
          <Filter id={ids.objective} label="Objetivo" value={objectiveId} onChange={onFilter(setObjectiveId)}>
            <option value="">Todos os objetivos</option>
            {opts?.objectives.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Filter>
          <Filter id={ids.department} label="Área" value={departmentId} onChange={onFilter(setDepartmentId)}>
            <option value="">Todas as áreas</option>
            {opts?.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Filter>
          <Filter id={ids.owner} label="Responsável" value={ownerId} onChange={onFilter(setOwnerId)}>
            <option value="">Todos os responsáveis</option>
            {opts?.owners.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Filter>
          <Filter id={ids.kind} label="Tipo" value={kind} onChange={onFilter(setKind)}>
            <option value="">Todos os tipos</option>
            {KPI_KINDS.map((k) => (
              <option key={k} value={k}>
                {KPI_KIND_LABELS[k]}
              </option>
            ))}
          </Filter>
          <Filter id={ids.direction} label="Direção" value={direction} onChange={onFilter(setDirection)}>
            <option value="">Todas as direções</option>
            {KPI_DIRECTIONS.map((d) => (
              <option key={d} value={d}>
                {KPI_DIRECTION_LABELS[d]}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.source}
            label="Fonte de dados"
            value={dataSource}
            onChange={onFilter(setDataSource)}
          >
            <option value="">Todas as fontes</option>
            {KPI_DATA_SOURCES.map((s) => (
              <option key={s} value={s}>
                {KPI_DATA_SOURCE_LABELS[s]}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.periodicity}
            label="Periodicidade"
            value={periodicity}
            onChange={onFilter(setPeriodicity)}
          >
            <option value="">Todas as periodicidades</option>
            {KPI_PERIODICITIES.map((p) => (
              <option key={p} value={p}>
                {KPI_PERIODICITY_LABELS[p]}
              </option>
            ))}
          </Filter>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={archived}
            onChange={(e) => {
              setArchived(e.target.checked);
              setPage(1);
            }}
          />
          Ver só os arquivados
        </label>
      </div>

      {list.isPending ? (
        <LoadingState rows={5} label="Carregando os indicadores" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.total === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Gauge}
          title={
            filtered
              ? 'Nenhum indicador encontrado'
              : archived
                ? 'Nenhum indicador arquivado'
                : 'Nenhum indicador ainda'
          }
          description={
            filtered
              ? 'Nenhum indicador corresponde à busca e aos filtros escolhidos.'
              : 'Cadastre os indicadores que medem os objetivos: como são calculados, quem responde por eles e a direção desejada.'
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clearFilters}>
                Limpar filtros
              </Button>
            ) : can('create') && !archived ? (
              <Button onClick={() => setEditing('new')} disabled={!opts}>
                <Plus aria-hidden />
                Novo indicador
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-3" aria-busy={list.isFetching}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.total === 1 ? '1 indicador' : `${list.data.total} indicadores`}
          </p>
          <ul aria-label="Indicadores do projeto" className="flex flex-col gap-2">
            {list.data.items.map((k) => (
              <li key={k.id} className="rounded-lg border border-border bg-surface p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="font-mono">
                        {k.code}
                      </Badge>
                      <p className="font-semibold">{k.name}</p>
                      <Badge variant="neutral">{KPI_KIND_LABELS[k.kind]}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{k.description}</p>
                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <Meta
                        icon={Gauge}
                        label="Medição"
                        value={`${kpiUnitLabel(k)} · ${KPI_PERIODICITY_LABELS[k.periodicity]}${k.periodicity === 'CUSTOM' && k.customPeriodDays ? ` (${k.customPeriodDays} dias)` : ''}`}
                      />
                      <Meta icon={Target} label="Direção" value={directionText(k)} />
                      <Meta icon={UserRound} label="Responsável" value={k.owner.name} />
                      <Meta icon={Users} label="Área" value={k.department.name} />
                      <Meta
                        icon={CalendarRange}
                        label="Vigência"
                        value={`${formatDate(k.validFrom)} a ${k.validTo ? formatDate(k.validTo) : 'sem prazo'}`}
                      />
                    </dl>
                    <p className="mt-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{k.perspective.name}</span>
                      {' · mede '}
                      {k.objectives.length
                        ? k.objectives
                            .map(
                              (o) =>
                                `${o.name}${o.weight !== null ? ` (peso ${formatPercent(o.weight)})` : ''}${o.archived ? ' (arquivado)' : ''}`,
                            )
                            .join('; ')
                        : 'objetivos fora do seu escopo'}
                      {k.objectives.length && k.hiddenObjectives
                        ? ` + ${k.hiddenObjectives} fora do seu escopo`
                        : ''}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Semáforo:{' '}
                      {k.trafficMode === 'COMPANY'
                        ? 'regra da empresa'
                        : `próprio — verde a partir de ${formatPercent(k.greenMin!)}, amarelo a partir de ${formatPercent(k.yellowMin!)}`}
                      {' · Fonte: '}
                      {KPI_DATA_SOURCE_LABELS[k.dataSource]}
                      {' · Acumulado: '}
                      {KPI_AGGREGATION_LABELS[k.aggregation]}
                    </p>
                    {k.dataSource === 'CALCULATED' && k.calculationFormula ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Cálculo automático:{' '}
                        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                          {k.calculationFormula}
                        </code>
                      </p>
                    ) : null}
                  </div>
                  <div className="flex gap-1 sm:shrink-0">
                    {canDo('metas-resultados', 'view') ? (
                      <Button asChild variant="ghost" size="icon-sm">
                        <Link
                          to={`/empresas/${organizationId}/indicadores/historico?kpi=${k.id}`}
                          aria-label={`Histórico de ${k.name}`}
                          title="Histórico"
                        >
                          <History aria-hidden />
                        </Link>
                      </Button>
                    ) : null}
                    <AuditLink
                      organizationId={organizationId}
                      scope={{ kpi: k.id, name: `${k.code} — ${k.name}` }}
                      label={`Auditoria de ${k.name}`}
                      compact
                    />
                    {!k.archivedAt && can('edit') ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${k.name}`}
                        onClick={() => setEditing(k)}
                        disabled={!opts}
                      >
                        <Pencil aria-hidden />
                      </Button>
                    ) : null}
                    {can('delete') ? (
                      k.archivedAt ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={toggleArchive.isPending}
                          onClick={() => toggleArchive.mutate(k)}
                        >
                          <ArchiveRestore aria-hidden />
                          Restaurar
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Arquivar ${k.name}`}
                          onClick={() => setArchiving(k)}
                        >
                          <Archive aria-hidden />
                        </Button>
                      )
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {list.data.totalPages > 1 ? (
            <nav aria-label="Paginação de indicadores" className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Página {list.data.page} de {list.data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= list.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </nav>
          ) : null}
        </div>
      )}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Novo indicador' : 'Editar indicador'}
        className="w-[min(48rem,calc(100vw-2rem))]"
      >
        {editing !== null && opts ? (
          <KpiForm
            organizationId={organizationId}
            initial={editing === 'new' ? undefined : editing}
            options={opts}
            defaultObjectiveId={objectiveId || undefined}
            canCreateUnit={canDo('indicadores', 'create')}
            onUnitCreated={() => queryClient.invalidateQueries({ queryKey: ['kpis', projectId, 'options'] })}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar o indicador?"
        description={`${archiving?.name ?? ''} sai das listas ativas. O histórico fica guardado e você pode restaurá-lo depois.`}
        confirmLabel="Arquivar indicador"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </div>
  );
}

function directionText(k: Kpi): string {
  if (k.direction === 'RANGE')
    return `Faixa ideal de ${formatNumber(k.rangeLower!)} a ${formatNumber(k.rangeUpper!)}`;
  if (k.direction === 'CUSTOM_FORMULA') return `Fórmula: ${k.performanceFormula}`;
  return KPI_DIRECTION_LABELS[k.direction];
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        Filtrar por {label.toLowerCase()}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}

function Meta({ icon: Icon, label, value }: { icon: typeof Target; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1">
      <Icon className="size-3.5" aria-hidden />
      <dt className="sr-only">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Sugestão de código a partir do nome ("Receita líquida" → "RECEITA_LIQUIDA"). */
function codeFromName(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9_]+/, '')
    .slice(0, 30);
  return base.length >= 2 ? base : '';
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="mb-1 text-sm font-bold">{title}</legend>
      {children}
    </fieldset>
  );
}

function KpiForm({
  organizationId,
  initial,
  options,
  defaultObjectiveId,
  canCreateUnit,
  onUnitCreated,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  organizationId: string;
  initial?: Kpi;
  options: KpiOptions;
  defaultObjectiveId?: string;
  canCreateUnit: boolean;
  onUnitCreated: () => Promise<unknown>;
  onSubmit: (input: KpiInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const defaultObjective = options.objectives.find((o) => o.id === defaultObjectiveId);
  const [code, setCode] = useState(initial?.code ?? '');
  const [codeTouched, setCodeTouched] = useState(Boolean(initial));
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [kind, setKind] = useState<KpiKindName>(initial?.kind ?? 'STRATEGIC');
  const [perspectiveId, setPerspectiveId] = useState(
    initial?.perspective.id ?? defaultObjective?.perspectiveId ?? options.perspectives[0]?.id ?? '',
  );
  const [departmentId, setDepartmentId] = useState(initial?.department.id ?? '');
  const [ownerId, setOwnerId] = useState(initial?.owner.id ?? '');
  const [unit, setUnit] = useState<KpiUnitName | ''>(initial?.unit ?? '');
  const [customUnitId, setCustomUnitId] = useState(initial?.customUnit?.id ?? '');
  const [periodicity, setPeriodicity] = useState<KpiPeriodicityName | ''>(initial?.periodicity ?? 'MONTHLY');
  const [customPeriodDays, setCustomPeriodDays] = useState(
    initial?.customPeriodDays ? String(initial.customPeriodDays) : '',
  );
  const [dataSource, setDataSource] = useState<KpiDataSourceName>(initial?.dataSource ?? 'MANUAL');
  const [direction, setDirection] = useState<KpiDirectionName>(initial?.direction ?? 'HIGHER_BETTER');
  const [rangeLower, setRangeLower] = useState(
    initial?.rangeLower === null || initial?.rangeLower === undefined ? '' : String(initial.rangeLower),
  );
  const [rangeUpper, setRangeUpper] = useState(
    initial?.rangeUpper === null || initial?.rangeUpper === undefined ? '' : String(initial.rangeUpper),
  );
  const [performanceFormula, setPerformanceFormula] = useState(initial?.performanceFormula ?? '');
  const [calculationFormula, setCalculationFormula] = useState(initial?.calculationFormula ?? '');
  const calculationInput = useRef<HTMLInputElement>(null);
  const caretAfterInsert = useRef<number | null>(null);
  const [formula, setFormula] = useState(initial?.formula ?? '');
  const [aggregation, setAggregation] = useState<KpiAggregationName | ''>(initial?.aggregation ?? '');
  const [validFrom, setValidFrom] = useState(initial?.validFrom ?? '');
  const [validTo, setValidTo] = useState(initial?.validTo ?? '');
  const [trafficMode, setTrafficMode] = useState(initial?.trafficMode ?? 'COMPANY');
  const [greenMin, setGreenMin] = useState(
    initial?.greenMin === null || initial?.greenMin === undefined ? '' : String(initial.greenMin),
  );
  const [yellowMin, setYellowMin] = useState(
    initial?.yellowMin === null || initial?.yellowMin === undefined ? '' : String(initial.yellowMin),
  );
  // Vínculos com objetivos fora do escopo de área de quem edita: não aparecem, mas são mantidos.
  const hidden = initial?.hiddenObjectives ?? 0;
  const [links, setLinks] = useState<{ objectiveId: string; weight: string }[]>(() => {
    const visible = (initial?.objectives ?? []).map((o) => ({
      objectiveId: o.id,
      weight: o.weight === null ? '' : String(o.weight),
    }));
    if (visible.length || hidden) return visible;
    return [{ objectiveId: defaultObjective?.id ?? '', weight: '' }];
  });
  const [customUnits, setCustomUnits] = useState<KpiCustomUnit[]>(options.customUnits);
  const [creatingUnit, setCreatingUnit] = useState(false);
  const [unitBusy, setUnitBusy] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const id = (k: string) => `kpi-${k}`;
  const uid = useId();
  const f = (k: string) => `${uid}-${id(k)}`;

  // O que o indicador já usa continua escolhível mesmo que tenha sido arquivado depois.
  const departments =
    initial && !options.departments.some((d) => d.id === initial.department.id)
      ? [...options.departments, { ...initial.department, parentId: null }]
      : options.departments;
  const owners =
    initial && !options.owners.some((o) => o.id === initial.owner.id)
      ? [...options.owners, initial.owner]
      : options.owners;
  const units =
    initial?.customUnit && !customUnits.some((u) => u.id === initial.customUnit!.id)
      ? [...customUnits, { ...initial.customUnit, aggregation: initial.aggregation, archivedAt: null }]
      : customUnits;
  const linkable = [
    ...options.objectives,
    ...(initial?.objectives ?? [])
      .filter((o) => !options.objectives.some((x) => x.id === o.id))
      .map((o) => ({ id: o.id, name: `${o.name}${o.archived ? ' (arquivado)' : ''}`, perspectiveId: '' })),
  ];
  const perspectiveName = (pid: string) => options.perspectives.find((p) => p.id === pid)?.name;
  // Indicadores que a fórmula de cálculo pode usar: os ativos do projeto no escopo, menos este.
  const formulaKpis = options.kpis.filter((k) => k.id !== initial?.id);
  function insertReference(kpiCode: string) {
    const input = calculationInput.current;
    const token = `[${kpiCode}]`;
    const start = input?.selectionStart ?? calculationFormula.length;
    const end = input?.selectionEnd ?? calculationFormula.length;
    setCalculationFormula(calculationFormula.slice(0, start) + token + calculationFormula.slice(end));
    caretAfterInsert.current = start + token.length;
  }
  // Depois de inserir um código, o cursor fica logo após ele, no campo.
  useLayoutEffect(() => {
    const caret = caretAfterInsert.current;
    if (caret === null) return;
    caretAfterInsert.current = null;
    calculationInput.current?.focus();
    calculationInput.current?.setSelectionRange(caret, caret);
  }, [calculationFormula]);
  const defaultAggregation =
    unit === 'CUSTOM'
      ? units.find((u) => u.id === customUnitId)?.aggregation
      : unit
        ? DEFAULT_AGGREGATION[unit]
        : undefined;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const num = (v: string) => (v.trim() === '' ? null : v.trim().replace(',', '.'));
    const parsed = kpiInputSchema.safeParse({
      code,
      name,
      description,
      kind,
      perspectiveId,
      departmentId: departmentId || undefined,
      ownerId: ownerId || undefined,
      unit: unit || undefined,
      customUnitId: unit === 'CUSTOM' ? customUnitId || null : null,
      periodicity: periodicity || undefined,
      customPeriodDays: periodicity === 'CUSTOM' ? num(customPeriodDays) : null,
      dataSource,
      direction,
      rangeLower: direction === 'RANGE' ? num(rangeLower) : null,
      rangeUpper: direction === 'RANGE' ? num(rangeUpper) : null,
      performanceFormula: direction === 'CUSTOM_FORMULA' ? performanceFormula : null,
      calculationFormula: dataSource === 'CALCULATED' ? calculationFormula : null,
      formula,
      aggregation: aggregation || null,
      validFrom,
      validTo: validTo || null,
      trafficMode,
      greenMin: trafficMode === 'CUSTOM' ? num(greenMin) : null,
      yellowMin: trafficMode === 'CUSTOM' ? num(yellowMin) : null,
      objectives: links
        .filter((l) => l.objectiveId)
        .map((l) => ({ objectiveId: l.objectiveId, weight: num(l.weight) })),
    });
    // RN-01: ao menos um objetivo, contando os que ficam fora do escopo de quem edita.
    const noObjective =
      hidden === 0 && !links.some((l) => l.objectiveId)
        ? { objectives: 'Ligue o indicador a pelo menos um objetivo (RN-01).' }
        : {};
    // Fórmula de cálculo: os códigos precisam ser de indicadores ativos que você vê (a API confere de novo).
    const unknown =
      parsed.success && parsed.data.calculationFormula
        ? calculationFormulaReferences(parsed.data.calculationFormula).filter(
            (c) => c !== parsed.data.code && !formulaKpis.some((k) => k.code === c),
          )
        : [];
    const badReference: FieldErrors = unknown.length
      ? {
          calculationFormula: `Indicador não encontrado entre os ativos deste projeto que você acessa: ${unknown.map((c) => `[${c}]`).join(', ')}.`,
        }
      : {};
    if (!parsed.success) return setErrors({ ...zodFieldErrors(parsed.error), ...noObjective });
    if (noObjective.objectives || badReference.calculationFormula) {
      return setErrors({ ...noObjective, ...badReference });
    }
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  const linkError =
    errors.objectives ?? Object.entries(errors).find(([k]) => k.startsWith('objectives.'))?.[1];

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <Section title="Identificação">
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <FormField id={f('name')} label="Nome" error={errors.name}>
            <Input
              {...fieldA11y(f('name'), errors.name)}
              value={name}
              maxLength={160}
              placeholder="Ex.: Receita líquida"
              onChange={(e) => {
                setName(e.target.value);
                if (!codeTouched) setCode(codeFromName(e.target.value));
              }}
              className="h-11"
            />
          </FormField>
          <FormField
            id={f('code')}
            label="Código"
            error={errors.code}
            hint={<p className="text-xs text-muted-foreground">Usado nas fórmulas de outros indicadores.</p>}
          >
            <Input
              {...fieldA11y(f('code'), errors.code, true)}
              value={code}
              maxLength={30}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(e.target.value.toUpperCase());
              }}
              className="h-11 font-mono"
            />
          </FormField>
        </div>
        <FormField id={f('description')} label="Descrição" error={errors.description}>
          <textarea
            {...fieldA11y(f('description'), errors.description)}
            value={description}
            maxLength={2000}
            rows={2}
            placeholder="O que o indicador mede e por que importa"
            onChange={(e) => setDescription(e.target.value)}
            className={textareaClass}
          />
        </FormField>
        <FormField id={f('kind')} label="Tipo" error={errors.kind}>
          <NativeSelect
            {...fieldA11y(f('kind'), errors.kind)}
            value={kind}
            onChange={(e) => setKind(e.target.value as KpiKindName)}
          >
            {KPI_KINDS.map((k) => (
              <option key={k} value={k}>
                {KPI_KIND_LABELS[k]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </Section>

      <Section title="O que ele mede">
        <FormField id={f('perspective')} label="Perspectiva" error={errors.perspectiveId}>
          <NativeSelect
            {...fieldA11y(f('perspective'), errors.perspectiveId)}
            value={perspectiveId}
            onChange={(e) => setPerspectiveId(e.target.value)}
          >
            {!options.perspectives.some((p) => p.id === perspectiveId) && initial ? (
              <option value={initial.perspective.id}>{initial.perspective.name}</option>
            ) : null}
            {options.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <fieldset aria-describedby={`${f('links')}-dica`}>
          <legend className="text-sm font-medium">Objetivos medidos</legend>
          <p id={`${f('links')}-dica`} className="mt-1 text-xs text-muted-foreground">
            Pelo menos um, e pelo menos um da perspectiva escolhida. O peso é quanto este indicador conta no
            objetivo (vazio = sem peso definido).
          </p>
          <ul className="mt-2 flex flex-col gap-2">
            {links.map((l, i) => (
              <li key={i} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <NativeSelect
                    aria-label={`Objetivo ${i + 1}`}
                    value={l.objectiveId}
                    onChange={(e) =>
                      setLinks((all) =>
                        all.map((x, j) => (j === i ? { ...x, objectiveId: e.target.value } : x)),
                      )
                    }
                  >
                    <option value="">Escolha o objetivo</option>
                    {linkable
                      .filter((o) => o.id === l.objectiveId || !links.some((x) => x.objectiveId === o.id))
                      .map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                          {o.perspectiveId && o.perspectiveId !== perspectiveId
                            ? ` — ${perspectiveName(o.perspectiveId)}`
                            : ''}
                        </option>
                      ))}
                  </NativeSelect>
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor={`${f('weight')}-${i}`} className="text-xs text-muted-foreground">
                    Peso
                  </label>
                  <Input
                    id={`${f('weight')}-${i}`}
                    aria-label={`Peso no objetivo ${i + 1} (%)`}
                    value={l.weight}
                    inputMode="decimal"
                    maxLength={6}
                    onChange={(e) =>
                      setLinks((all) => all.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)))
                    }
                    className="h-11 w-20 text-right"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover objetivo ${i + 1}`}
                    disabled={links.length === 1 && hidden === 0}
                    onClick={() => setLinks((all) => all.filter((_, j) => j !== i))}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={links.length >= linkable.length}
              onClick={() => setLinks((all) => [...all, { objectiveId: '', weight: '' }])}
            >
              <Plus aria-hidden />
              Adicionar objetivo
            </Button>
            {hidden ? (
              <p className="text-xs text-muted-foreground">
                {hidden === 1
                  ? 'Também mede 1 objetivo de área fora do seu acesso; ele é mantido.'
                  : `Também mede ${hidden} objetivos de áreas fora do seu acesso; eles são mantidos.`}
              </p>
            ) : null}
          </div>
          {linkError ? <p className="mt-1.5 text-sm font-medium text-status-red-fg">{linkError}</p> : null}
        </fieldset>
      </Section>

      <Section title="Responsabilidade">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id={f('department')} label="Área" error={errors.departmentId}>
            <NativeSelect
              {...fieldA11y(f('department'), errors.departmentId)}
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <option value="">Escolha a área</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={f('owner')} label="Responsável" error={errors.ownerId}>
            <NativeSelect
              {...fieldA11y(f('owner'), errors.ownerId)}
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            >
              <option value="">Escolha a pessoa</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        </div>
      </Section>

      <Section title="Medição">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id={f('unit')} label="Unidade de medida" error={errors.unit}>
            <NativeSelect
              {...fieldA11y(f('unit'), errors.unit)}
              value={unit}
              onChange={(e) => setUnit(e.target.value as KpiUnitName | '')}
            >
              <option value="">Escolha a unidade</option>
              {KPI_UNITS.map((u) => (
                <option key={u} value={u}>
                  {KPI_UNIT_LABELS[u]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          {unit === 'CUSTOM' ? (
            <FormField id={f('custom-unit')} label="Unidade customizada" error={errors.customUnitId}>
              <div className="flex gap-2">
                <div className="flex-1">
                  <NativeSelect
                    {...fieldA11y(f('custom-unit'), errors.customUnitId)}
                    value={customUnitId}
                    onChange={(e) => setCustomUnitId(e.target.value)}
                  >
                    <option value="">
                      {units.length ? 'Escolha a unidade' : 'Nenhuma unidade cadastrada'}
                    </option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                        {u.symbol ? ` (${u.symbol})` : ''}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                {canCreateUnit ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11"
                    onClick={() => setCreatingUnit(true)}
                  >
                    <Plus aria-hidden />
                    Nova
                  </Button>
                ) : null}
              </div>
            </FormField>
          ) : null}
          <FormField
            id={f('periodicity')}
            label="Periodicidade"
            error={errors.periodicity}
            hint={
              dataSource === 'CALCULATED' ? (
                <p className="text-xs text-muted-foreground">
                  Indicador calculado é mensal: usa o realizado do mês das entradas.
                </p>
              ) : undefined
            }
          >
            <NativeSelect
              {...fieldA11y(f('periodicity'), errors.periodicity, dataSource === 'CALCULATED')}
              value={periodicity}
              disabled={dataSource === 'CALCULATED'}
              onChange={(e) => setPeriodicity(e.target.value as KpiPeriodicityName | '')}
            >
              {KPI_PERIODICITIES.map((p) => (
                <option key={p} value={p}>
                  {KPI_PERIODICITY_LABELS[p]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          {periodicity === 'CUSTOM' ? (
            <FormField id={f('days')} label="Intervalo (dias)" error={errors.customPeriodDays}>
              <Input
                {...fieldA11y(f('days'), errors.customPeriodDays)}
                value={customPeriodDays}
                inputMode="numeric"
                maxLength={4}
                onChange={(e) => setCustomPeriodDays(e.target.value)}
                className="h-11 w-28"
              />
            </FormField>
          ) : null}
          <FormField id={f('source')} label="Fonte de dados" error={errors.dataSource}>
            <NativeSelect
              {...fieldA11y(f('source'), errors.dataSource)}
              value={dataSource}
              onChange={(e) => {
                const next = e.target.value as KpiDataSourceName;
                setDataSource(next);
                if (next === 'CALCULATED') setPeriodicity('MONTHLY');
              }}
            >
              {KPI_DATA_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {KPI_DATA_SOURCE_LABELS[s]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField
            id={f('aggregation')}
            label="Acumulado no ano"
            error={errors.aggregation}
            hint={
              <p className="text-xs text-muted-foreground">
                {defaultAggregation
                  ? `Padrão da unidade: ${KPI_AGGREGATION_LABELS[defaultAggregation].toLowerCase()}.`
                  : 'Padrão conforme a unidade.'}
              </p>
            }
          >
            <NativeSelect
              {...fieldA11y(f('aggregation'), errors.aggregation, true)}
              value={aggregation}
              onChange={(e) => setAggregation(e.target.value as KpiAggregationName | '')}
            >
              <option value="">Padrão da unidade</option>
              {KPI_AGGREGATIONS.map((a) => (
                <option key={a} value={a}>
                  {KPI_AGGREGATION_LABELS[a]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        </div>
        {dataSource === 'CALCULATED' ? (
          <FormField
            id={f('calculation')}
            label="Cálculo automático"
            error={errors.calculationFormula}
            hint={
              <p className="text-xs text-muted-foreground">
                Use o código dos indicadores entre colchetes, + − * / ^, parênteses e min, max, abs, round,
                sqrt. Ex.: [RECEITA] / [CLIENTES]. O valor de cada mês sai do realizado do mês das entradas.
              </p>
            }
          >
            <Input
              {...fieldA11y(f('calculation'), errors.calculationFormula, true)}
              ref={calculationInput}
              value={calculationFormula}
              maxLength={1000}
              placeholder="[RECEITA] / [CLIENTES]"
              onChange={(e) => setCalculationFormula(e.target.value)}
              className="h-11 font-mono"
            />
          </FormField>
        ) : null}
        {dataSource === 'CALCULATED' ? (
          formulaKpis.length ? (
            <div>
              <p id={`${f('calculation')}-codigos`} className="text-xs font-medium">
                Indicadores disponíveis (clique para inserir na fórmula)
              </p>
              <ul
                aria-labelledby={`${f('calculation')}-codigos`}
                className="mt-1.5 flex max-h-36 flex-wrap gap-1.5 overflow-y-auto"
              >
                {formulaKpis.map((k) => (
                  <li key={k.id}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={`Inserir [${k.code}] — ${k.name}`}
                      title={k.name}
                      onClick={() => insertReference(k.code)}
                      className="font-mono"
                    >
                      [{k.code}]
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Nenhum outro indicador ativo neste projeto para usar na fórmula: cadastre as entradas primeiro.
            </p>
          )
        ) : null}
        <FormField
          id={f('formula')}
          label="Fórmula de cálculo"
          error={errors.formula}
          hint={
            <p className="text-xs text-muted-foreground">
              Como o valor é obtido (ex.: receita bruta − deduções).
            </p>
          }
        >
          <textarea
            {...fieldA11y(f('formula'), errors.formula, true)}
            value={formula}
            maxLength={1000}
            rows={2}
            onChange={(e) => setFormula(e.target.value)}
            className={textareaClass}
          />
        </FormField>
      </Section>

      <Section title="Direção desejada">
        <FormField id={f('direction')} label="Direção" error={errors.direction}>
          <NativeSelect
            {...fieldA11y(f('direction'), errors.direction)}
            value={direction}
            onChange={(e) => setDirection(e.target.value as KpiDirectionName)}
          >
            {KPI_DIRECTIONS.map((d) => (
              <option key={d} value={d}>
                {KPI_DIRECTION_LABELS[d]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        {direction === 'RANGE' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id={f('lower')} label="Limite inferior da faixa" error={errors.rangeLower}>
              <Input
                {...fieldA11y(f('lower'), errors.rangeLower)}
                value={rangeLower}
                inputMode="decimal"
                onChange={(e) => setRangeLower(e.target.value)}
                className="h-11"
              />
            </FormField>
            <FormField id={f('upper')} label="Limite superior da faixa" error={errors.rangeUpper}>
              <Input
                {...fieldA11y(f('upper'), errors.rangeUpper)}
                value={rangeUpper}
                inputMode="decimal"
                onChange={(e) => setRangeUpper(e.target.value)}
                className="h-11"
              />
            </FormField>
          </div>
        ) : null}
        {direction === 'CUSTOM_FORMULA' ? (
          <FormField
            id={f('performance')}
            label="Fórmula de performance"
            error={errors.performanceFormula}
            hint={
              <p className="text-xs text-muted-foreground">
                Use realizado e meta, + − * / ^, parênteses e min, max, abs, round, sqrt. Ex.: min(100,
                realizado / meta * 100).
              </p>
            }
          >
            <Input
              {...fieldA11y(f('performance'), errors.performanceFormula, true)}
              value={performanceFormula}
              maxLength={500}
              onChange={(e) => setPerformanceFormula(e.target.value)}
              className="h-11 font-mono"
            />
          </FormField>
        ) : null}
      </Section>

      <Section title="Vigência e semáforo">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id={f('from')} label="Vigente a partir de" error={errors.validFrom}>
            <Input
              {...fieldA11y(f('from'), errors.validFrom)}
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              className="h-11"
            />
          </FormField>
          <FormField id={f('to')} label="Vigente até (opcional)" error={errors.validTo}>
            <Input
              {...fieldA11y(f('to'), errors.validTo)}
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              className="h-11"
            />
          </FormField>
        </div>
        <FormField id={f('traffic')} label="Regra de semáforo" error={errors.trafficMode}>
          <NativeSelect
            {...fieldA11y(f('traffic'), errors.trafficMode)}
            value={trafficMode}
            onChange={(e) => setTrafficMode(e.target.value as 'COMPANY' | 'CUSTOM')}
          >
            <option value="COMPANY">Regra da empresa</option>
            <option value="CUSTOM">Regra própria deste indicador</option>
          </NativeSelect>
        </FormField>
        {trafficMode === 'CUSTOM' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id={f('green')} label="Verde a partir de (% da meta)" error={errors.greenMin}>
              <Input
                {...fieldA11y(f('green'), errors.greenMin)}
                value={greenMin}
                inputMode="decimal"
                maxLength={6}
                onChange={(e) => setGreenMin(e.target.value)}
                className="h-11 w-28"
              />
            </FormField>
            <FormField id={f('yellow')} label="Amarelo a partir de (% da meta)" error={errors.yellowMin}>
              <Input
                {...fieldA11y(f('yellow'), errors.yellowMin)}
                value={yellowMin}
                inputMode="decimal"
                maxLength={6}
                onChange={(e) => setYellowMin(e.target.value)}
                className="h-11 w-28"
              />
            </FormField>
          </div>
        ) : null}
      </Section>

      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar indicador' : 'Cadastrar indicador'}
        </Button>
      </div>

      <Dialog
        open={creatingUnit}
        onOpenChange={setCreatingUnit}
        busy={unitBusy}
        title="Nova unidade de medida"
      >
        {creatingUnit ? (
          <CustomUnitForm
            organizationId={organizationId}
            onCancel={() => setCreatingUnit(false)}
            onBusyChange={setUnitBusy}
            onCreated={async (u) => {
              setCustomUnits((all) => [...all, u].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
              setCustomUnitId(u.id);
              setCreatingUnit(false);
              toast.success('Unidade criada', { description: u.name });
              await onUnitCreated();
            }}
          />
        ) : null}
      </Dialog>
    </form>
  );
}

function CustomUnitForm({
  organizationId,
  onCreated,
  onCancel,
  onBusyChange,
}: {
  organizationId: string;
  onCreated: (unit: KpiCustomUnit) => Promise<void>;
  onCancel: () => void;
  /** Enquanto envia, o diálogo não fecha (Esc, clique fora, X). */
  onBusyChange: (busy: boolean) => void;
}) {
  const { client } = useAuth();
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [aggregation, setAggregation] = useState<KpiAggregationName>('SUM');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), symbol: useId(), aggregation: useId() };

  // Formulário dentro do formulário do KPI: o envio não pode disparar o de fora.
  async function submit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (saving) return;
    setFormError(null);
    const parsed = kpiCustomUnitInputSchema.safeParse({ name, symbol, aggregation });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      const created = await client.request(`/organizations/${organizationId}/kpi-units`, {
        method: 'POST',
        body: parsed.data,
        schema: kpiCustomUnitSchema,
      });
      await onCreated(created);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <FormField id={ids.name} label="Nome da unidade" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={60}
          placeholder="Ex.: Pallets"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.symbol} label="Símbolo (opcional)" error={errors.symbol}>
        <Input
          {...fieldA11y(ids.symbol, errors.symbol)}
          value={symbol}
          maxLength={10}
          placeholder="Ex.: plt"
          onChange={(e) => setSymbol(e.target.value)}
          className="h-11 w-32"
        />
      </FormField>
      <FormField id={ids.aggregation} label="Acumulado no ano" error={errors.aggregation}>
        <NativeSelect
          {...fieldA11y(ids.aggregation, errors.aggregation)}
          value={aggregation}
          onChange={(e) => setAggregation(e.target.value as KpiAggregationName)}
        >
          {KPI_AGGREGATIONS.map((a) => (
            <option key={a} value={a}>
              {KPI_AGGREGATION_LABELS[a]}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          Criar unidade
        </Button>
      </div>
    </form>
  );
}
